"use client";
import { useRouter } from "next/navigation";

import { useClientConfigProvider } from "@/app/config/client-config-provider";
import { EventLabels } from "@/app/constants/mix-panel-enums";
import AppRoutes from "@/app/constants/routes";
import { useMixPanel } from "@/app/context/mixPanelContext";
import isLoggedIn from "@/app/redirect";
import ToastUtils from "@/app/toast/toast-utils";
import {
  UniversalStorageConstants,
  UniversalStorageUtils,
  useAppContext,
} from "@thriwe-2-0/thriwe-ui-utils";
import { SessionStorageUtils } from "@thriwe-2-0/thriwe-ui-utils";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";

import StringConstants from "@/app/constants/string-contants";
import PreLoginCustomHeader from "@/app/versioning/components/v2/CustomHeader/custom_header";
import { encryptPassword } from "@/app/versioning/encryption";
import { IoArrowBackSharp } from "react-icons/io5";
import { WebUI } from "./LeftPanel";
import { LoginForm } from "./LoginForm";
export interface LoginInfo {
  countryCode?: string;
  mobile?: string;
  token?: string;
  email?: string;
  fromWhere: string;
}
export interface CountryCodeType {
  code: string;
  flag: any;
  currency: string;
  name: string;
}

export enum LoginType {
  Mobile,
  Email,
  MobileAndEmail,
}

function LoginWithPasswordV2({
  placeHolderText,
  toastType,
  islogin = true,
}: any) {
  const context = useAppContext();
  const { trackEvent } = useMixPanel();
  const {
    instances: { apiRepository },
    state: { isMobile },
    fetchUserProfile,
  } = context;
  const { config, getImagePath } = useClientConfigProvider();
  const router = useRouter();

  const [countryCode, setCountryCode] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [mobileNumber, setMobileNumber] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [isVerified, setIsVerified] = useState<boolean>(false);

  const [switchSignInType, setswitchSignInType] = useState<string>("email");
  const [validationSchema, setValidationSchema] = useState<any>(null);

  useEffect(() => {
    if (switchSignInType == "email" && islogin) {
      trackEvent(EventLabels.SL_Login_Email, { screen: "Login" });
      trackEvent(EventLabels.SL_Login_Email_Done, { screen: "Login" });
    }
  }, [switchSignInType, trackEvent]);

  useEffect(() => {
    if (config) {
      if (switchSignInType == "mobile" && config?.login?.fields?.mobile) {
        setValidationSchema(
          Yup.object().shape({
            mobile: Yup.string()
              .required(`${config?.login?.fields?.mobile?.validationText}`)
              .matches(
                config?.login?.fields?.mobile?.validationRegex,
                config?.login?.fields?.mobile?.validationText
              ),
            password: Yup.string().required("Password is a required field"),
          })
        );
      } else if (switchSignInType == "email" && config?.login?.fields?.email) {
        setValidationSchema(
          Yup.object().shape({
            email: Yup.string()
              .required("Email is a required field")
              .matches(
                config?.login?.fields?.email?.validationRegex,
                config?.login?.fields?.email?.validationText
              ),
            password: Yup.string().required("Password is a required field"),
          })
        );
      }

      (() => loginForm.validateForm())();
      loginForm.setFieldValue(
        switchSignInType == "mobile" ? "email" : "mobile",
        ""
      );
    }
  }, [switchSignInType, config]);

  const handleFormSubmit = (values: any) => {
    setIsLoading(true);
    if (config?.login.checkUserBeforeLogin) {
      apiRepository
        ?.checkUser({
          email: values?.email,
          phone: values?.mobile.toString(),
          countryCode: countryCode?.toString(),
        })
        .then((v) => {
          if (v.data.exists == true) {
            handleLoginFormSubmit(values);
            SessionStorageUtils.saveToSessionStorage(
              StringConstants.PARAM_X_REGION,
              v?.data?.region
            );
          } else if (v.data.exists == false) {
            setIsLoading(false);
            ToastUtils.showErrorToast(v?.data?.message ?? "");
          } else {
            setIsLoading(false);
            ToastUtils.showErrorToast("Something went wrong");
          }
        })
        .catch((e) => {
          setIsLoading(false);
          console.log("login error response", e?.response?.data);
          if (e?.response?.data?.message) {
            ToastUtils.showErrorToast(e?.response?.data?.message);
          } else {
            ToastUtils.showErrorToast("Something went wrong");
          }
        });
    } else {
      handleLoginFormSubmit(values);
    }
  };

  const handleLoginFormSubmit = async (values: any) => {
    setIsLoading(true);
    const mobileNumber = values.mobile;
    setEmail(values?.email);
    setMobileNumber(mobileNumber);
    setIsLoading(true);
    const passwordToUse = config?.login?.isEncrypted
      ? await encryptPassword(values?.password)
      : values?.password;

    apiRepository
      ?.loginRegisterPassword({
        email: values?.email,
        phone: values?.mobile.toString(),
        countryCode: countryCode.toString(),
        password: passwordToUse,
      })
      .then((v) => {
        ToastUtils.showSuccessToast(v?.data?.message);

        UniversalStorageUtils.setItem(
          config?.nativeDataSavingType,
          UniversalStorageConstants.AUTH_TOKEN,
          v.headers.authorization
        );

        setTimeout(() => {
          console.log("otploginwithpassword2");
          router.push(AppRoutes.dashboard);
        }, 500);

        setTimeout(() => {
          setIsVerified(true);
        }, 100);

        fetchUserProfile();
      })
      .catch((e) => {
        setIsLoading(false);

        if (e?.response?.data?.message) {
          ToastUtils.showErrorToast(e?.response?.data?.message);
        } else {
          ToastUtils.showErrorToast("Something went wrong");
        }
      });
  };

  const loginForm = useFormik({
    initialValues: {
      mobile: "",
      email: "",
      password: "",
    },
    isInitialValid: false,
    validationSchema: validationSchema,
    onSubmit: handleFormSubmit,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnMount: true,
    validateOnChange: true,
  });

  const { isValid } = loginForm;

  const handleDivClick = (handleFormSubmit: any) => {
    handleFormSubmit();
    trackEvent(islogin ? EventLabels.CTA_LOGIN : EventLabels.CTA_Registration, {
      meta:
        switchSignInType == "email" ? "Login with Email" : "Login with Mobile",
      screen: "Login",
    });
  };

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <WebUI>
      <div className="right-login-desktop-container">
        <div className="form-screen-v2">
          <div className="overlay-v2 display_mobile">
            <img
              src={
                isMobile
                  ? getImagePath("v2/login/login_bg_dark_mobile.png")
                  : getImagePath("v2/login/login_bg.webp")
              }
              alt="Logo"
              className="bg-login-v2"
            />
          </div>
          <div className="back_button_prelogin display_desktop">
            <button
              className="back-btn-v2"
              onClick={() => {
                // router.back();
                router.push(AppRoutes.landing);
              }}
            >
              <IoArrowBackSharp size={24} />
            </button>
          </div>

          {config?.preLoginCustomHeader?.isShowCustomHeader && (
            <PreLoginCustomHeader />
          )}
          <LoginForm
            placeHolderText={"Login"}
            loginForm={loginForm}
            buttonRef={buttonRef}
            setShowPassword={setShowPassword}
            showPassword={showPassword}
            handleDivClick={handleDivClick}
            isLoading={isLoading}
          />
        </div>
      </div>
    </WebUI>
  );
}

export default isLoggedIn(LoginWithPasswordV2);
